import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, Shield, AlertTriangle, CheckCircle, XCircle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const healthColors = {
  ok: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-400" },
  warning: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", dot: "bg-amber-400" },
  critical: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", dot: "bg-red-400" },
};

const HealthIcon = ({ health, size = 16 }) => {
  if (health === "ok") return <CheckCircle size={size} className="text-emerald-400" />;
  if (health === "warning") return <AlertTriangle size={size} className="text-amber-400" />;
  return <XCircle size={size} className="text-red-400" />;
};

export default function IntegrationsHealthPanel({ token }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealth = useCallback(async (refresh = false) => {
    try {
      const r = await fetch(`${API}/admin/integrations-health${refresh ? "?refresh=true" : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (r.ok) {
        const data = await r.clone().json();
        setReport(data);
      }
    } catch (e) {
      console.error("Health check error:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(() => fetchHealth(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchHealth(true);
  };

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6 animate-pulse">
        <div className="h-6 bg-slate-700 rounded w-48 mb-4" />
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-slate-700/50 rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (!report) return null;

  const { summary, checks = [], checked_at } = report;
  const overallColor = healthColors[summary?.overall] || healthColors.critical;
  const lastCheck = checked_at ? new Date(checked_at) : null;
  const minutesAgo = lastCheck ? Math.round((Date.now() - lastCheck.getTime()) / 60000) : null;

  return (
    <div data-testid="integrations-health-panel" className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
      {/* Header */}
      <div className={`px-5 py-3.5 flex items-center justify-between ${overallColor.bg} border-b ${overallColor.border}`}>
        <div className="flex items-center gap-3">
          <Shield size={18} className={overallColor.text} />
          <h3 className="text-sm font-semibold text-slate-200">Sante des integrations</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${overallColor.bg} ${overallColor.text} border ${overallColor.border}`}>
            {summary?.ok || 0}/{summary?.total || 0} OK
          </span>
        </div>
        <div className="flex items-center gap-3">
          {minutesAgo !== null && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Clock size={12} />
              {minutesAgo < 1 ? "a l'instant" : `il y a ${minutesAgo} min`}
            </span>
          )}
          <button
            data-testid="refresh-health-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
            title="Rafraichir"
          >
            <RefreshCw size={14} className={`text-slate-400 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Alerts banner */}
      {summary?.overall !== "ok" && (
        <div className={`px-5 py-2 ${overallColor.bg} border-b ${overallColor.border} flex items-center gap-2`}>
          <AlertTriangle size={14} className={overallColor.text} />
          <span className={`text-xs font-medium ${overallColor.text}`}>
            {summary?.critical > 0 && `${summary.critical} critique${summary.critical > 1 ? "s" : ""}`}
            {summary?.critical > 0 && summary?.warning > 0 && " — "}
            {summary?.warning > 0 && `${summary.warning} avertissement${summary.warning > 1 ? "s" : ""}`}
          </span>
        </div>
      )}

      {/* Checks grid */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
        {checks.map((check) => {
          const color = healthColors[check.health] || healthColors.critical;
          return (
            <div
              key={check.id}
              data-testid={`health-${check.id}`}
              className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg ${color.bg} border ${color.border} transition-all hover:brightness-110`}
            >
              <div className="mt-0.5 shrink-0">
                <HealthIcon health={check.health} size={15} />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-200 truncate">{check.name}</div>
                <div className={`text-[11px] ${color.text} leading-tight mt-0.5`}>{check.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
